import { z } from "zod";

/**
 * Catalogue de blocs Alchimie — source unique de vérité.
 * Utilisé pour les Leçons (Lesson.blocks) ET les Pages de vente (SalesPage.blocks).
 *
 * Validation Zod côté API + types TypeScript inférés pour le frontend.
 *
 * Chaque bloc a la forme : { type, id, props, style }
 *   - type   : discriminator (HERO, VIDEO, RICH_TEXT, …)
 *   - id     : identifiant unique du bloc (pour React keys + drag-and-drop)
 *   - props  : données métier propres au type
 *   - style  : styles visuels (padding, couleurs, alignement, etc.)
 */

// ─────────────────────────────────────────────────────────────
// Style — appliqué à n'importe quel bloc
// ─────────────────────────────────────────────────────────────
export const BlockStyleSchema = z.object({
  // Espacements (CSS values)
  paddingTop: z.string().optional(),
  paddingBottom: z.string().optional(),
  paddingLeft: z.string().optional(),
  paddingRight: z.string().optional(),
  marginTop: z.string().optional(),
  marginBottom: z.string().optional(),
  // Fond
  backgroundColor: z.string().optional(),     // hex / rgba / "transparent"
  backgroundImage: z.string().optional(),     // url(...)
  backgroundSize: z.string().optional(),
  backgroundPosition: z.string().optional(),
  backgroundOverlay: z.string().optional(),   // rgba pour assombrir l'image
  // Texte
  textColor: z.string().optional(),
  subtitleColor: z.string().optional(), // sous-titre / texte secondaire ; sinon hérite de textColor avec un peu d'opacité
  textAlign: z.enum(["left", "center", "right"]).optional(),
  fontFamily: z.string().optional(),
  // Container
  maxWidth: z.string().optional(),
  borderRadius: z.string().optional(),
  borderColor: z.string().optional(),
  borderWidth: z.string().optional(),
  // Animations (entrée)
  enterAnimation: z.enum(["none", "fade", "slide-up", "slide-left", "scale"]).optional(),
  enterDelay: z.number().optional(), // ms
});
export type BlockStyle = z.infer<typeof BlockStyleSchema>;

// ─────────────────────────────────────────────────────────────
// Référence à un Asset (vidéo, audio, PDF, image)
// ─────────────────────────────────────────────────────────────
export const AssetRefSchema = z.object({
  assetId: z.string(),
});
export type AssetRef = z.infer<typeof AssetRefSchema>;

// ─────────────────────────────────────────────────────────────
// Schémas par type de bloc
// ─────────────────────────────────────────────────────────────

const BaseFields = {
  id: z.string(),
  style: BlockStyleSchema.optional(),
};

export const HeroBlockSchema = z.object({
  ...BaseFields,
  type: z.literal("HERO"),
  props: z.object({
    title: z.string(),
    subtitle: z.string().optional(),
    backgroundAssetId: z.string().optional(), // image de fond
    ctaLabel: z.string().optional(),
    ctaAction: z.enum(["buy", "scroll-to", "external"]).optional(),
    ctaTarget: z.string().optional(), // pour scroll-to (id) ou external (url)
  }),
});

export const RichTextBlockSchema = z.object({
  ...BaseFields,
  type: z.literal("RICH_TEXT"),
  props: z.object({
    // Stocké au format Tiptap JSON pour édition + rendu sécurisé
    // (compatible HTML rendering via @tiptap/react)
    doc: z.unknown(),
  }),
});

export const VideoBlockSchema = z.object({
  ...BaseFields,
  type: z.literal("VIDEO"),
  props: z.object({
    assetId: z.string(),
    autoplay: z.boolean().default(false),
    loop: z.boolean().default(false),
    caption: z.string().optional(),
  }),
});

export const AudioBlockSchema = z.object({
  ...BaseFields,
  type: z.literal("AUDIO"),
  props: z.object({
    assetId: z.string(),
    title: z.string().optional(),
    transcript: z.string().optional(),
  }),
});

export const PdfBlockSchema = z.object({
  ...BaseFields,
  type: z.literal("PDF"),
  props: z.object({
    assetId: z.string(),
    title: z.string().optional(),
    showPreview: z.boolean().default(true),
    downloadable: z.boolean().default(true),
  }),
});

export const ImageBlockSchema = z.object({
  ...BaseFields,
  type: z.literal("IMAGE"),
  props: z.object({
    assetId: z.string(),
    alt: z.string().optional(),
    caption: z.string().optional(),
    href: z.string().url().optional(),
  }),
});

export const BenefitsBlockSchema = z.object({
  ...BaseFields,
  type: z.literal("BENEFITS"),
  props: z.object({
    heading: z.string().optional(),
    columns: z.number().int().min(1).max(4).default(3),
    items: z.array(
      z.object({
        icon: z.string().optional(), // emoji ou nom d'icône
        title: z.string(),
        body: z.string().optional(),
      })
    ),
  }),
});

export const TestimonialsBlockSchema = z.object({
  ...BaseFields,
  type: z.literal("TESTIMONIALS"),
  props: z.object({
    heading: z.string().optional(),
    items: z.array(
      z.object({
        quote: z.string(),
        author: z.string(),
        role: z.string().optional(),
      })
    ),
  }),
});

export const FaqBlockSchema = z.object({
  ...BaseFields,
  type: z.literal("FAQ"),
  props: z.object({
    heading: z.string().optional(),
    items: z.array(z.object({ q: z.string(), a: z.string() })),
  }),
});

export const PricingBlockSchema = z.object({
  ...BaseFields,
  type: z.literal("PRICING"),
  props: z.object({
    heading: z.string().optional(),
    description: z.string().optional(),
    ctaLabel: z.string().default("Rejoindre maintenant"),
    showBullets: z.array(z.string()).optional(),
  }),
});

export const CtaBlockSchema = z.object({
  ...BaseFields,
  type: z.literal("CTA"),
  props: z.object({
    label: z.string(),
    subtitle: z.string().optional(),
    action: z.enum(["buy", "scroll-to", "external"]).default("buy"),
    target: z.string().optional(),
  }),
});

export const DividerBlockSchema = z.object({
  ...BaseFields,
  type: z.literal("DIVIDER"),
  props: z.object({
    color: z.string().optional(),         // hex / rgba — par défaut #e0dcd0
    thickness: z.string().optional(),     // ex : "1px", "2px"
    style: z.enum(["solid", "dashed", "dotted"]).optional(),
  }).default({}),
});

export const SpacerBlockSchema = z.object({
  ...BaseFields,
  type: z.literal("SPACER"),
  props: z.object({
    height: z.string().default("48px"),
  }),
});

// Pour Phase A : on accepte n'importe quelle structure interne au runtime
// (validation récursive ajoutée en Phase D quand le builder utilisera vraiment ce bloc).
export const ColumnsBlockSchema = z.object({
  ...BaseFields,
  type: z.literal("COLUMNS"),
  props: z.object({
    columns: z.array(z.array(z.unknown())),
    // Poids relatifs (ex : [1, 2, 1]). Optionnel — équilibré par défaut.
    widths: z.array(z.number().positive()).optional(),
    // Espace entre colonnes (CSS), ex "24px".
    gap: z.string().optional(),
  }),
});

export const CustomHtmlBlockSchema = z.object({
  ...BaseFields,
  type: z.literal("CUSTOM_HTML"),
  props: z.object({
    html: z.string(),
  }),
});

// ─────────────────────────────────────────────────────────────
// Union discriminée — type de tous les blocs
// ─────────────────────────────────────────────────────────────
export const BlockSchema = z.discriminatedUnion("type", [
  HeroBlockSchema,
  RichTextBlockSchema,
  VideoBlockSchema,
  AudioBlockSchema,
  PdfBlockSchema,
  ImageBlockSchema,
  BenefitsBlockSchema,
  TestimonialsBlockSchema,
  FaqBlockSchema,
  PricingBlockSchema,
  CtaBlockSchema,
  DividerBlockSchema,
  SpacerBlockSchema,
  CustomHtmlBlockSchema,
  ColumnsBlockSchema,
]);

export type HeroBlock = z.infer<typeof HeroBlockSchema>;
export type RichTextBlock = z.infer<typeof RichTextBlockSchema>;
export type VideoBlock = z.infer<typeof VideoBlockSchema>;
export type AudioBlock = z.infer<typeof AudioBlockSchema>;
export type PdfBlock = z.infer<typeof PdfBlockSchema>;
export type ImageBlock = z.infer<typeof ImageBlockSchema>;
export type BenefitsBlock = z.infer<typeof BenefitsBlockSchema>;
export type TestimonialsBlock = z.infer<typeof TestimonialsBlockSchema>;
export type FaqBlock = z.infer<typeof FaqBlockSchema>;
export type PricingBlock = z.infer<typeof PricingBlockSchema>;
export type CtaBlock = z.infer<typeof CtaBlockSchema>;
export type DividerBlock = z.infer<typeof DividerBlockSchema>;
export type SpacerBlock = z.infer<typeof SpacerBlockSchema>;
export type CustomHtmlBlock = z.infer<typeof CustomHtmlBlockSchema>;
// Pour les colonnes, le type statique est plus précis que la validation runtime
// (qui se contente d'accepter `unknown[][]` car Zod ne fait pas de récursion ici).
export interface ColumnsBlock {
  id: string;
  type: "COLUMNS";
  style?: BlockStyle;
  props: { columns: Block[][]; widths?: number[]; gap?: string };
}

export type Block =
  | HeroBlock
  | RichTextBlock
  | VideoBlock
  | AudioBlock
  | PdfBlock
  | ImageBlock
  | BenefitsBlock
  | TestimonialsBlock
  | FaqBlock
  | PricingBlock
  | CtaBlock
  | DividerBlock
  | SpacerBlock
  | CustomHtmlBlock
  | ColumnsBlock;

export type BlockType = Block["type"];

// ─────────────────────────────────────────────────────────────
// Validation d'un tableau de blocs (utilisé côté API)
// ─────────────────────────────────────────────────────────────
export const BlocksArraySchema = z.array(BlockSchema);

// ─────────────────────────────────────────────────────────────
// Breakpoints — surcharges responsive
// ─────────────────────────────────────────────────────────────
export type Breakpoint = "desktop" | "tablet" | "mobile";

export const BreakpointOverridesSchema = z.record(
  z.string(), // blockId
  z.object({
    tablet: BlockStyleSchema.partial().optional(),
    mobile: BlockStyleSchema.partial().optional(),
  })
);
export type BreakpointOverrides = z.infer<typeof BreakpointOverridesSchema>;

// ─────────────────────────────────────────────────────────────
// Style global d'une page (typo, couleur d'accent)
// ─────────────────────────────────────────────────────────────
export const PageStyleSchema = z.object({
  fontFamily: z.string().optional(),
  accentColor: z.string().optional(),
  bodyColor: z.string().optional(),
  backgroundColor: z.string().optional(),
});
export type PageStyle = z.infer<typeof PageStyleSchema>;

// ─────────────────────────────────────────────────────────────
// Catalogue des types : libellés + métadonnées d'affichage
// (Utilisé par la palette du Builder)
// ─────────────────────────────────────────────────────────────
export const BLOCK_CATALOG: Record<BlockType, { label: string; icon: string; group: "structure" | "media" | "marketing" | "advanced" }> = {
  HERO:         { label: "Hero",           icon: "✨", group: "structure" },
  RICH_TEXT:    { label: "Texte",          icon: "T",  group: "structure" },
  VIDEO:        { label: "Vidéo",          icon: "▶",  group: "media" },
  AUDIO:        { label: "Audio",          icon: "♫",  group: "media" },
  PDF:          { label: "PDF",            icon: "📄", group: "media" },
  IMAGE:        { label: "Image",          icon: "🖼", group: "media" },
  BENEFITS:     { label: "Bénéfices",      icon: "★",  group: "marketing" },
  TESTIMONIALS: { label: "Témoignages",    icon: "❝",  group: "marketing" },
  FAQ:          { label: "FAQ",            icon: "?",  group: "marketing" },
  PRICING:      { label: "Prix",           icon: "€",  group: "marketing" },
  CTA:          { label: "Bouton",         icon: "→",  group: "marketing" },
  DIVIDER:      { label: "Séparateur",     icon: "—",  group: "structure" },
  SPACER:       { label: "Espace",         icon: "▢",  group: "structure" },
  COLUMNS:      { label: "Colonnes",       icon: "‖‖", group: "structure" },
  CUSTOM_HTML:  { label: "Code HTML",      icon: "</>",group: "advanced" },
};
